// Grafo interactivo de entidades + conexiones con vis-network.
// Click en nodo → callback onSelectEntity(entity).
// Click en edge → callback onSelectConnection(connection).

import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Network } from 'vis-network';
import { DataSet } from 'vis-data';
import {
  borderSoft,
  panelDeep,
  accent,
  mono,
} from '../../styles/techComponents';

const TYPE_COLOR = {
  person:        { bg: '#00b4d8', border: '#0094b8' },
  company:       { bg: '#f4a261', border: '#d18443' },
  organization:  { bg: '#a78bfa', border: '#7c63d6' },
  place:         { bg: '#06d6a0', border: '#04a87f' },
  event:         { bg: '#ef476f', border: '#c93558' },
  concept:       { bg: '#fbbf24', border: '#d1981a' },
};

const KIND_COLOR = {
  family:      '#ef476f',
  business:    '#f4a261',
  political:   '#00b4d8',
  employment:  '#a78bfa',
  financial:   '#06d6a0',
  legal:       '#fbbf24',
  social:      '#9ca3af',
  historical:  '#7e7e7e',
  rivalry:     '#ef4444',
  other:       '#6b7280',
};

const GraphWrap = styled.div`
  position: relative;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  height: 600px;
  overflow: hidden;
`;

const Legend = styled.div`
  position: absolute;
  bottom: 10px; left: 12px;
  background: rgba(11, 19, 32, 0.85);
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  padding: 8px 10px;
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-disabled);
  z-index: 5;

  .row { display: flex; gap: 6px; align-items: center; margin-bottom: 4px; }
  .row:last-child { margin-bottom: 0; }
  .dot { width: 10px; height: 10px; border-radius: 50%; }
`;

const Toolbar = styled.div`
  position: absolute;
  top: 10px; right: 12px;
  display: flex; gap: 6px;
  z-index: 5;

  button {
    background: rgba(11, 19, 32, 0.85);
    border: 1px solid ${borderSoft};
    color: var(--text-secondary);
    padding: 4px 10px;
    border-radius: 5px;
    font-size: 0.72rem;
    cursor: pointer;
    &:hover { color: ${accent}; border-color: rgba(0, 180, 216, 0.4); }
  }
`;

const Empty = styled.div`
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  color: var(--text-disabled);
  font-size: 0.9rem;
  text-align: center;
  padding: 2rem;
`;

export default function EntityGraph({ entities, connections, onSelectEntity, onSelectConnection }) {
  const containerRef = useRef(null);
  const networkRef = useRef(null);
  const nodesDsRef = useRef(null);
  const edgesDsRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    if (!entities || entities.length === 0) return;

    const nodes = entities.map((e) => {
      const palette = TYPE_COLOR[e.type] || TYPE_COLOR.concept;
      const size = 14 + Math.min(40, (e.importance || 0) / 100 * 24);
      return {
        id: e.id,
        label: e.name,
        title: `${e.name} (${e.type})${e.summary ? '\n\n' + e.summary.slice(0, 200) : ''}`,
        shape: e.type === 'place' ? 'triangle' : e.type === 'event' ? 'star' : 'dot',
        size,
        color: { background: palette.bg, border: palette.border, highlight: { background: palette.bg, border: '#fff' } },
        font: { color: '#e6e9ef', size: 13, face: 'Inter, system-ui, sans-serif' },
        borderWidth: 2,
        _raw: e,
      };
    });

    const edges = (connections || []).map((c) => ({
      id: c.id,
      from: c.from_entity_id,
      to: c.to_entity_id,
      label: c.label || c.kind,
      title: `${c.from_name} → ${c.to_name}\n${c.kind}${c.label ? ' · ' + c.label : ''}\nstrength: ${c.strength}`,
      color: { color: KIND_COLOR[c.kind] || '#6b7280', highlight: '#fff' },
      width: 1 + (c.strength || 50) / 100 * 4,
      arrows: { to: { enabled: true, scaleFactor: 0.5 } },
      font: { color: '#9ca3af', size: 10, face: 'JetBrains Mono, monospace', strokeWidth: 0, align: 'middle' },
      smooth: { type: 'continuous', roundness: 0.2 },
      _raw: c,
    }));

    nodesDsRef.current = new DataSet(nodes);
    edgesDsRef.current = new DataSet(edges);

    const options = {
      physics: {
        enabled: true,
        barnesHut: { gravitationalConstant: -8000, springLength: 160, springConstant: 0.04, damping: 0.4 },
        stabilization: { iterations: 200, fit: true },
      },
      interaction: { hover: true, tooltipDelay: 120, navigationButtons: false, keyboard: { enabled: false } },
      nodes: { borderWidth: 2, shadow: { enabled: true, color: 'rgba(0,0,0,0.35)', size: 8 } },
      edges: { selectionWidth: 1.5, smooth: { type: 'continuous' } },
    };

    const network = new Network(
      containerRef.current,
      { nodes: nodesDsRef.current, edges: edgesDsRef.current },
      options
    );
    networkRef.current = network;

    network.on('selectNode', (params) => {
      if (params.nodes?.length > 0 && onSelectEntity) {
        const id = params.nodes[0];
        const ent = entities.find((e) => e.id === id);
        if (ent) onSelectEntity(ent);
      }
    });
    network.on('selectEdge', (params) => {
      if (params.edges?.length > 0 && onSelectConnection) {
        const id = params.edges[0];
        const conn = connections.find((c) => c.id === id);
        if (conn) onSelectConnection(conn);
      }
    });

    return () => {
      try { network.destroy(); } catch {}
    };
  }, [entities, connections, onSelectEntity, onSelectConnection]);

  if (!entities || entities.length === 0) {
    return (
      <GraphWrap>
        <Empty>
          Sin entidades para graficar.<br />
          Agregá al menos una entidad (Tab "Entidades") o dispará el Entity Researcher.
        </Empty>
      </GraphWrap>
    );
  }

  return (
    <GraphWrap>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <Legend>
        <div className="row"><span className="dot" style={{ background: TYPE_COLOR.person.bg }} /> persona</div>
        <div className="row"><span className="dot" style={{ background: TYPE_COLOR.company.bg }} /> empresa</div>
        <div className="row"><span className="dot" style={{ background: TYPE_COLOR.organization.bg }} /> organización</div>
        <div className="row"><span className="dot" style={{ background: TYPE_COLOR.place.bg }} /> lugar</div>
        <div className="row"><span className="dot" style={{ background: TYPE_COLOR.event.bg }} /> evento</div>
        <div className="row"><span className="dot" style={{ background: TYPE_COLOR.concept.bg }} /> concepto</div>
      </Legend>
      <Toolbar>
        <button type="button" onClick={() => networkRef.current?.fit({ animation: { duration: 600, easingFunction: 'easeOutQuart' } })}>
          ⛶ Encuadrar
        </button>
        <button type="button" onClick={() => networkRef.current?.setOptions({ physics: { enabled: !networkRef.current.physics.options.enabled } })}>
          ⏯ Pausar física
        </button>
      </Toolbar>
    </GraphWrap>
  );
}
